import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { getStoredUser } from "@/lib/auth";
import { useTenant } from "@/contexts/TenantContext";
import { buildTenantPath } from "@/lib/tenantPath";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";


import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import {
  Plus,
  Play,
  Pencil,
  Trash2,
  Copy,
  FileDown,
  ArrowLeft,
  FlaskConical,
} from "lucide-react";
import { usePageTitle } from "@/contexts/PageTitleContext";
import AiScriptWizardDialog from "@/components/ai/AiScriptWizardDialog";



function ScriptCard({ script, canEdit, canUseSandbox, onDelete, onDuplicate, userId }) {
  const sandboxPrefKey = `sandbox_pref:${userId || "anon"}:${script.id}`;
  const [sandboxMode, setSandboxMode] = useState(() => {
    return localStorage.getItem(sandboxPrefKey) === "true";
  });

  const onToggleSandbox = (checked) => {
    setSandboxMode(checked);
    localStorage.setItem(sandboxPrefKey, checked ? "true" : "false");
  };
  
  return (
    <Card className="glass" data-testid={`script-card-${script.id}`}>
      <CardHeader className="relative flex flex-col justify-between space-y-0 p-4 pr-16 min-h-[156px]">
        <div className="min-w-0">
          <CardTitle className="truncate text-slate-100" data-testid={`script-card-title-${script.id}`}>
            {script.name}
          </CardTitle>
          {script.description ? (
            <div className="mt-2 muted text-sm" data-testid={`script-card-description-${script.id}`}>
              {script.description}
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid={`script-card-actions-${script.id}`}>
          {/* Run */}
          <Button
            asChild
            data-testid={`script-card-play-button-${script.id}`}
          >
            <Link to={`/app/scripts/${script.id}/play${sandboxMode ? "?sandbox=true" : ""}`}>
              <Play className="mr-2 h-4 w-4" />
              Run
            </Link>
          </Button>

          {/* Sandbox Toggle - Only for Admins/Super Admins */}
          {canUseSandbox && (
            <div
              className={
                "flex items-center gap-2 rounded-full px-3 py-2 ring-1 " +
                (sandboxMode
                  ? "bg-amber-500/20 ring-amber-400/30"
                  : "bg-white/5 ring-white/10")
              }
              data-testid={`sandbox-toggle-container-${script.id}`}
            >
              <span className={`text-xs ${sandboxMode ? "text-amber-300" : "text-slate-300"}`}>
                Sandbox
              </span>
              <Switch
                checked={sandboxMode}
                onCheckedChange={onToggleSandbox}
                variant="sandbox"
                data-testid={`sandbox-toggle-${script.id}`}
              />
            </div>
          )}
        </div>

        {/* Right-side vertical action rail (absolute so it doesn't stretch card layout) */}
        <div
          className="absolute right-4 top-4 flex flex-col items-center gap-2"
          data-testid={`script-card-actions-rail-${script.id}`}
        >
          {canEdit ? (
            <Button
              asChild
              variant="outline"
              size="icon"
              data-testid={`script-card-edit-button-${script.id}`}
              title="Edit"
            >
              <Link to={`/app/scripts/${script.id}/edit`}>
                <Pencil className="h-4 w-4" />
              </Link>
            </Button>
          ) : (
            <Button
              variant="outline"
              size="icon"
              disabled
              data-testid={`script-card-edit-disabled-button-${script.id}`}
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}

          {canEdit ? (
            <Button
              variant="outline"
              size="icon"
              onClick={() => onDuplicate(script.id)}
              data-testid={`script-card-duplicate-button-${script.id}`}
              title="Duplicate"
            >
              <Copy className="h-4 w-4" />
            </Button>
          ) : null}

          {canEdit ? (
            <Button
              variant="outline"
              size="icon"
              onClick={() => onDelete(script.id)}
              data-testid={`script-card-delete-button-${script.id}`}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </CardHeader>
    </Card>
  );
}

export default function PlaybookScriptsPage() {
  const { playbookId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const user = getStoredUser();
  const isSuper = user?.role === "super_admin";
  
  // P0 TENANT ISOLATION: Use global tenant context
  const { selectedTenantId } = useTenant();
  
  // P0 TENANT ISOLATION: Use centralized tenant path builder
  const buildPath = useCallback((basePath) => {
    return buildTenantPath(basePath, selectedTenantId);
  }, [selectedTenantId]);

  const [productType, setProductType] = useState(null);
  const [scriptTypes, setScriptTypes] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeScriptTypeId, setActiveScriptTypeId] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // New Script (AI) flow
  const [newScriptFlowOpen, setNewScriptFlowOpen] = useState(false);
  const [aiWizardOpen, setAiWizardOpen] = useState(false);

  const [llmKeyStatus, setLlmKeyStatus] = useState({ openai: false, anthropic: false });

  usePageTitle(productType?.name || "Playbooks");

  const [aiProvider, setAiProvider] = useState("openai");
  const [aiModel, setAiModel] = useState("gpt-5.1");
  const [aiApiKey, setAiApiKey] = useState("");

  const [aiTitle, setAiTitle] = useState("");
  const [aiDescription, setAiDescription] = useState("");
  const [aiGoal, setAiGoal] = useState("");
  const [aiAudience, setAiAudience] = useState("");
  const [aiTone, setAiTone] = useState("Professional & Calm");
  const [aiToneCustom, setAiToneCustom] = useState("");

  const [aiConversationStyle, setAiConversationStyle] = useState("");
  const [aiConversationStyleCustom, setAiConversationStyleCustom] = useState("");

  const [aiStagesText, setAiStagesText] = useState("Intro\nQualify\nPresent\nClose");
  const [aiObjectionsText, setAiObjectionsText] = useState("Too expensive\nNeed to think about it\nTalk to spouse");

  // Step 4/5 - row-based field editor
  const [aiDataFields, setAiDataFields] = useState([
    {
      id: crypto.randomUUID(),
      label_mode: "preset",
      preset_label: "First Name",
      custom_label: "",
      field_type: "text",
      scope: "prospects",
      required: true,
      options_text: "",
    },
    {
      id: crypto.randomUUID(),
      label_mode: "preset",
      preset_label: "Phone",
      custom_label: "",
      field_type: "phone",
      scope: "prospects",
      required: true,
      options_text: "",
    },
  ]);

  const [aiQuoteFields, setAiQuoteFields] = useState([
    {
      id: crypto.randomUUID(),
      label_mode: "preset",
      preset_label: "Coverage Amount",
      custom_label: "",
      kind: "input",
      field_type: "number",
      include_in_household: true,
      show_in_household: false,
      options_text: "",
    },
    {
      id: crypto.randomUUID(),
      label_mode: "preset",
      preset_label: "Monthly Premium",
      custom_label: "",
      kind: "input",
      field_type: "number",
      include_in_household: true,
      show_in_household: false,
      options_text: "",
    },
  ]);

  const [aiStep, setAiStep] = useState(1);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiSpec, setAiSpec] = useState(null);

  // MA-ARCH-142: Message context for ScriptAssist integration
  const [aiMessageCategory, setAiMessageCategory] = useState("");
  const [aiMessageVariant, setAiMessageVariant] = useState("");
  const [aiOpeningHook, setAiOpeningHook] = useState("");
  // MA-ARCH-142A: Loading start time for progress display
  const [aiGeneratingStartTime, setAiGeneratingStartTime] = useState(null);

  const [createScriptTypeOpen, setCreateScriptTypeOpen] = useState(false);
  const [newScriptTypeName, setNewScriptTypeName] = useState("");

  const onBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {

      navigate("/app");
    }
  };

  const label = "Playbook";

  const dataFieldLabelPresets = [
    "First Name",
    "Last Name",
    "Phone",
    "Email",
    "Date of Birth",
    "Address",
    "City",
    "State",
    "ZIP",
    "Mortgage Balance",
    "Monthly Payment",
    "Current Provider",
  ];

  const quoteFieldLabelPresets = [
    "Company",
    "Insurance Type",
    "Coverage Amount",
    "Monthly Premium",
    "Annual Premium",
    "Term Length",
  ];

  const fetchAll = async () => {
    setLoading(true);
    try {
      const ptRes = await api.get(`/product-types`, { params: {} });
      const pt = ptRes.data.find((p) => p.id === playbookId);
      setProductType(pt || null);

      const stRes = await api.get(`/product-types/${playbookId}/script-types`);
      setScriptTypes(stRes.data);
      const first = stRes.data?.[0]?.id || null;
      setActiveScriptTypeId((prev) => prev || first);
    } catch (e) {
      toast({
        title: "Failed to load product",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLlmKeyStatus = async () => {
    try {
      const res = await api.get("/llm-keys");
      const st = { openai: false, anthropic: false };
      (res.data || []).forEach((row) => {
        if (row.provider === "openai") st.openai = !!row.connected;
        if (row.provider === "anthropic") st.anthropic = !!row.connected;
      });
      setLlmKeyStatus(st);
    } catch (e) {
      // silent
    }
  };

  const parseLines = (text) =>
    (text || "")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

  const normalizeLabel = (row) => {
    const mode = row?.label_mode || "preset";
    const preset = String(row?.preset_label || "").trim();
    const custom = String(row?.custom_label || "").trim();
    return mode === "custom" ? custom : preset;
  };

  const parseDataFieldRows = () => {
    return (aiDataFields || [])
      .map((r, idx) => {
        const label = normalizeLabel(r);
        if (!label) return null;
        const field_type = r.field_type || "text";
        const scope = r.scope || "household";
        const required = !!r.required;
        const options = String(r.options_text || "")
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean);
        return {
          label,
          field_type,
          scope,
          required,
          options: field_type === "dropdown" ? options : undefined,
          sort_order: idx + 1,
        };
      })
      .filter(Boolean);
  };

  const parseQuoteFieldRows = () => {
    return (aiQuoteFields || [])
      .map((r, idx) => {
        const label = normalizeLabel(r);
        if (!label) return null;
        const kind = r.kind || "input";
        const field_type = r.field_type || "text";
        const options = String(r.options_text || "")
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean);
        return {
          label,
          kind,
          field_type,
          include_in_household: kind === "input" ? !!r.include_in_household : undefined,
          show_in_household: kind === "calculated" ? !!r.show_in_household : undefined,
          options: field_type === "dropdown" ? options : undefined,
          sort_order: idx + 1,
        };
      })
      .filter(Boolean);
  };

  const resetAiWizard = () => {
    setAiStep(1);
    setAiGenerating(false);
    setAiSpec(null);
    setAiApiKey("");

    setAiTitle("");
    setAiDescription("");
    setAiGoal("");
    setAiAudience("");
    setAiTone("Professional & Calm");
    setAiToneCustom("");
    setAiConversationStyle("");
    setAiConversationStyleCustom("");

    setAiStagesText("Intro\nQualify\nPresent\nClose");
    setAiObjectionsText("Too expensive\nNeed to think about it\nTalk to spouse");

    setAiDataFields([
      {
        id: crypto.randomUUID(),
        label_mode: "preset",
        preset_label: "First Name",
        custom_label: "",
        field_type: "text",
        scope: "prospects",
        required: true,
        options_text: "",
      },
      {
        id: crypto.randomUUID(),
        label_mode: "preset",
        preset_label: "Phone",
        custom_label: "",
        field_type: "phone",
        scope: "prospects",
        required: true,
        options_text: "",
      },
    ]);

    setAiQuoteFields([
      {
        id: crypto.randomUUID(),
        label_mode: "preset",
        preset_label: "Coverage Amount",
        custom_label: "",
        kind: "input",
        field_type: "number",
        include_in_household: true,
        show_in_household: false,
        options_text: "",
      },
      {
        id: crypto.randomUUID(),
        label_mode: "preset",
        preset_label: "Monthly Premium",
        custom_label: "",
        kind: "input",
        field_type: "number",
        include_in_household: true,
        show_in_household: false,
        options_text: "",
      },
    ]);

  };

  const onOpenAiWizard = () => {
    resetAiWizard();
    // Open immediately; key status is non-blocking and can load in the background.
    setAiWizardOpen(true);
    fetchLlmKeyStatus();
  };

  const onGenerateAiSpec = async () => {
    try {
      setAiGenerating(true);
      // MA-ARCH-142A: Record start time for loading progress display
      setAiGeneratingStartTime(Date.now());
      const res = await api.post("/scripts/ai-generate", {
        provider: aiProvider,
        model: aiModel,
        product_type_id: playbookId,
        script_type_id: activeScriptTypeId,
        title: aiTitle || "AI Script",
        description: aiDescription || null,
        goal: aiGoal,
        audience: aiAudience,
        tone: aiTone === "Custom" ? (aiToneCustom || "Custom") : aiTone,
        conversation_style:
          aiConversationStyle === "Custom"
            ? (aiConversationStyleCustom || "Custom")
            : aiConversationStyle,
        stages: parseLines(aiStagesText),
        objections: parseLines(aiObjectionsText),
        data_fields: parseDataFieldRows(),
        quote_fields: parseQuoteFieldRows(),
        remember_key: true,
        api_key: aiApiKey || null,
        // MA-ARCH-142: Message context for aligned script generation
        message_category: aiMessageCategory || null,
        message_variant: aiMessageVariant || null,
        opening_hook: aiOpeningHook || null,
      });
      setAiSpec(res.data?.spec || null);
      setAiStep(6);
    } catch (e) {
      toast({
        title: "AI generation failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setAiGenerating(false);
      setAiGeneratingStartTime(null);
    }
  };

  const onCreateFromAiSpec = async () => {
    if (!aiSpec) return;
    try {
      setAiGenerating(true);
      const res = await api.post("/scripts/ai-create", {
        provider: aiProvider,
        model: aiModel,
        product_type_id: playbookId,
        script_type_id: activeScriptTypeId,
        spec: aiSpec,
        // MA-ARCH-142: Persist message context on created script
        message_category: aiMessageCategory || null,
        message_variant: aiMessageVariant || null,
        opening_hook: aiOpeningHook || null,
      });
      toast({ title: "AI script created" });
      setAiWizardOpen(false);
      await fetchScripts();
      navigate(buildPath(`/app/scripts/${res.data.script_id}/edit`));
    } catch (e) {
      toast({
        title: "Create failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setAiGenerating(false);
    }
  };

  const fetchScripts = async () => {
    if (!activeScriptTypeId) return;
    try {
      const res = await api.get(`/product-types/${playbookId}/scripts`, {
        params: { script_type_id: activeScriptTypeId },
      });
      setScripts(res.data);
    } catch (e) {
      toast({
        title: "Could not load scripts",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbookId]);

  useEffect(() => {
    fetchScripts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScriptTypeId]);

  const canEdit = user?.role === "super_admin" || user?.role === "admin";
  const canUseSandbox = user?.role === "super_admin" || user?.role === "admin";

  const onCreate = async () => {
    try {
      const res = await api.post(`/scripts`, {
        product_type_id: playbookId,
        script_type_id: activeScriptTypeId,
        name,
        description: description || null,
      });
      toast({ title: "Script created" });
      setCreateOpen(false);
      setName("");
      setDescription("");
      await fetchScripts();
      navigate(buildPath(`/app/scripts/${res.data.id}/edit`));
    } catch (e) {
      toast({
        title: "Create failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this script (and its nodes/options)?")) return;
    try {
      await api.delete(`/scripts/${id}`);
      toast({ title: "Script deleted" });
      await fetchScripts();
    } catch (e) {
      toast({
        title: "Delete failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  };

  const onDuplicate = async (id) => {
    try {
      const res = await api.post(`/scripts/${id}/duplicate`);
      toast({ title: "Duplicated" });
      await fetchScripts();
      navigate(buildPath(`/app/scripts/${res.data.id}/edit`));
    } catch (e) {
      toast({
        title: "Duplicate failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  };

  const onClickImport = () => {
    fileRef.current?.click();
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const json = JSON.parse(text);
      // enforce target product/script type context
      json.script = {
        ...(json.script || {}),
        product_type_id: playbookId,
        script_type_id: activeScriptTypeId,
      };
      const res = await api.post("/scripts/import", json);
      toast({ title: "Imported" });
      await fetchScripts();
      navigate(buildPath(`/app/scripts/${res.data.id}/edit`));
    } catch (err) {
      toast({
        title: "Import failed",
        description: err?.response?.data?.detail || err.message || "Invalid file.",
        variant: "destructive",
      });
    } finally {
      e.target.value = "";
    }
  };

  const exportTemplate = async () => {
    const template = {
      script: {
        name: "My Script",
        description: "",
        start_node_id: "node-1",
        product_type_id: playbookId,
        script_type_id: activeScriptTypeId,
      },
      nodes: [
        {
          id: "node-1",
          title: "Start",
          your_reply: "Hello — thanks for taking my call…",
          coaching_note: "Say it with calm confidence.",
        },
      ],
      options: [],
    };
    const blob = new Blob([JSON.stringify(template, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "script-template.json";
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Template downloaded" });
  };

  const onCreateScriptType = async () => {
    try {
      const res = await api.post(`/product-types/${playbookId}/script-types`, {
        name: newScriptTypeName,
      });
      toast({ title: "Script type created" });
      setCreateScriptTypeOpen(false);
      setNewScriptTypeName("");
      await fetchAll();
      setActiveScriptTypeId(res.data.id);
    } catch (e) {
      toast({
        title: "Create failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  };

  const onDeleteScriptType = async (id) => {
    if (!window.confirm("Delete this script type and ALL scripts under it?")) return;
    try {
      await api.delete(`/script-types/${id}`);
      toast({ title: "Script type deleted" });
      await fetchAll();
      setActiveScriptTypeId(null);
    } catch (e) {
      toast({
        title: "Delete failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6" data-testid="playbook-scripts-page">
      {/* Script actions moved into the Script Type bar */}

      {loading ? (
        <div className="muted" data-testid="playbook-scripts-loading-text">Loading…</div>
      ) : scriptTypes.length === 0 ? (
        <div className="muted" data-testid="playbook-scripts-no-script-types-text">
          No script types.
        </div>
      ) : (
        <>
          <div className="flex w-full items-center gap-2" data-testid="playbook-scripts-script-type-bar">
            <Tabs
              value={activeScriptTypeId || scriptTypes[0].id}
              onValueChange={setActiveScriptTypeId}
              className="w-full"
              data-testid="playbook-scripts-tabs"
            >
              <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 flex-1 items-center gap-2" data-testid="playbook-scripts-tabs-with-actions">
                  <Button
                    variant="ghost"
                    onClick={onBack}
                    data-testid="product-scripts-back-button"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <TabsList className="bg-indigo-500/20 text-indigo-100 ring-1 ring-indigo-400/20" data-testid="playbook-scripts-tabs-list">
                    {scriptTypes.map((st) => (
                      <TabsTrigger
                        key={st.id}
                        value={st.id}
                        className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100"
                        data-testid={`playbook-scripts-tab-${st.id}`}
                      >
                        {st.name}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {isSuper ? (
                    <div className="flex items-center gap-2 shrink-0" data-testid="playbook-scripts-script-type-actions-row">
                      <Dialog open={createScriptTypeOpen} onOpenChange={setCreateScriptTypeOpen}>
                            <DialogTrigger asChild>
                      <Button
                        variant="secondary"
                        size="icon"
                        data-testid="create-script-type-open-button"
                        title="Add script type"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                            </DialogTrigger>
                            <DialogContent className="glass border-white/10" data-testid="create-script-type-dialog">
                              <DialogHeader>
                                  <DialogTitle className="text-slate-100" data-testid="create-script-type-title">
                            Create script type
                                  </DialogTitle>
                                  <DialogDescription className="text-slate-200/70" data-testid="create-script-type-description">
                            Adds a new tab for this product type.
                                  </DialogDescription>
                              </DialogHeader>
                        <div className="space-y-2">
                          <label className="text-sm text-slate-200/80" data-testid="create-script-type-name-label">
                            Name
                          </label>
                          <Input
                            value={newScriptTypeName}
                            onChange={(e) => setNewScriptTypeName(e.target.value)}
                            placeholder="Rebuttals"
                            className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                            data-testid="create-script-type-name-input"
                          />
                        </div>
                              <DialogFooter>
                          <Button
                            onClick={onCreateScriptType}
                            disabled={!newScriptTypeName.trim()}
                            data-testid="create-script-type-submit-button"
                          >
                            Create
                          </Button>
                              </DialogFooter>
                          </DialogContent>
                      </Dialog>

                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => onDeleteScriptType(activeScriptTypeId)}
                        disabled={!activeScriptTypeId}
                        data-testid="delete-script-type-button"
                        title="Delete script type"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                </div>

                <div className="ml-auto shrink-0 flex justify-end" data-testid="playbook-scripts-new-script-cta-wrap">
                  <Dialog open={newScriptFlowOpen} onOpenChange={setNewScriptFlowOpen}>
                    <DialogTrigger asChild>
                      <Button
                        data-testid="playbook-scripts-create-script-open-button"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        New Script
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="glass border-white/10 sm:max-w-2xl" data-testid="new-script-flow-dialog">
                      <DialogHeader>
                        <DialogTitle className="text-slate-100 text-xl" data-testid="new-script-flow-title">
                          Create a Script
                        </DialogTitle>
                      </DialogHeader>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 py-4">
                        {/* Build Manually Card */}
                        <button
                          className="h-auto flex flex-col items-start justify-start text-left rounded-xl p-6 border-2 border-white/10 hover:border-slate-600/50 hover:bg-slate-800/30 transition-all group focus:outline-none focus:ring-2 focus:ring-slate-600"
                          onClick={() => {
                            setNewScriptFlowOpen(false);
                            setCreateOpen(true);
                          }}
                          data-testid="new-script-flow-manual-button"
                        >
                          <div className="flex items-center gap-3 mb-4">
                            <div className="p-2.5 rounded-lg bg-slate-700/30">
                              <svg className="h-6 w-6 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
                              </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-slate-100">Build Manually</h3>
                          </div>
                          
                          <p className="text-sm text-slate-200/70 mb-4">
                            Create your script step-by-step with full control.
                          </p>
                          
                          <p className="text-xs text-slate-200/60 mb-3">
                            ScriptAssist is available to help refine questions and responses as you build.
                          </p>
                          
                          <div className="space-y-2 mt-auto">
                            <div className="flex items-center gap-2 text-xs text-slate-200/60">
                              <svg className="h-3.5 w-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>Full control over script structure</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-200/60">
                              <svg className="h-3.5 w-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>Step-by-step editing</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-200/60">
                              <svg className="h-3.5 w-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>Ideal for advanced users</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-200/60">
                              <svg className="h-3.5 w-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>Can refine with ScriptAssist later</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-end mt-4 text-sm text-slate-400">
                            Start Building
                            <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                            </svg>
                          </div>
                        </button>

                        {/* Create with ScriptAssist Card */}
                        <button
                          className="h-auto flex flex-col items-start justify-start text-left rounded-xl p-6 border-2 border-indigo-500/30 hover:border-indigo-500/50 hover:bg-indigo-500/10 transition-all group focus:outline-none focus:ring-2 focus:ring-indigo-500 relative overflow-hidden"
                          onClick={() => {
                            setNewScriptFlowOpen(false);
                            onOpenAiWizard();
                          }}
                          data-testid="new-script-flow-ai-button"
                        >
                          <div className="flex items-center gap-3 mb-4">
                            <div className="p-2.5 rounded-lg bg-indigo-500/20">
                              <svg className="h-6 w-6 text-indigo-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                              </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-slate-100">Create with ScriptAssist AI</h3>
                          </div>
                          
                          <p className="text-sm text-slate-200/70 mb-4">
                            Describe your script and ScriptAssist will build the structure for you.
                          </p>
                          
                          <p className="text-xs text-slate-200/60 mb-3">
                            You'll review and approve each step before publishing.
                          </p>
                          
                          <div className="space-y-2 mt-auto">
                            <div className="flex items-center gap-2 text-xs text-slate-200/60">
                              <svg className="h-3.5 w-3.5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>Full script structure generation</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-200/60">
                              <svg className="h-3.5 w-3.5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>Question and branch creation</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-200/60">
                              <svg className="h-3.5 w-3.5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>Relevant data field generation</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-200/60">
                              <svg className="h-3.5 w-3.5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>Quote sheet support (when applicable)</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-end mt-4 text-sm text-indigo-400">
                            Generate
                            <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                            </svg>
                          </div>
                        </button>
                      </div>

                      <p className="text-xs text-slate-500 text-center pt-2">
                        Note: Script outputs should always be reviewed and adjusted before use.
                      </p>
                    </DialogContent>
                  </Dialog>

                  {/* Manual create dialog (existing flow) */}
                  <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                    <DialogContent
                      className="glass border-white/10"
                      data-testid="playbook-scripts-create-script-dialog"
                    >
                      <DialogHeader>
                        <DialogTitle
                          className="text-slate-100"
                          data-testid="playbook-scripts-create-script-title"
                        >
                          Create script
                        </DialogTitle>
                        <DialogDescription
                          className="text-slate-200/70"
                          data-testid="playbook-scripts-create-script-description"
                        >
                          Scripts live inside this product type and script type.
                        </DialogDescription>
                      </DialogHeader>

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label
                            className="text-sm text-slate-200/80"
                            data-testid="playbook-scripts-create-script-name-label"
                          >
                            Script title
                          </label>
                          <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Mortgage Protection — Opener"
                            className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                            data-testid="playbook-scripts-create-script-name-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <label
                            className="text-sm text-slate-200/80"
                            data-testid="playbook-scripts-create-script-description-label"
                          >
                            Description (optional)
                          </label>
                          <Textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="min-h-[90px] bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                            data-testid="playbook-scripts-create-script-description-input"
                          />
                        </div>
                      </div>

                      <DialogFooter>
                        <Button
                          type="button"
                          onClick={onCreate}
                          disabled={!name.trim() || !activeScriptTypeId}
                          data-testid="playbook-scripts-create-script-submit-button"
                        >
                          Create
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  <AiScriptWizardDialog
                    open={aiWizardOpen}
                    onOpenChange={setAiWizardOpen}
                    state={{
                      llmKeyStatus,
                      aiStep,
                      aiProvider,
                      aiModel,
                      aiApiKey,
                      aiTitle,
                      aiDescription,
                      aiGoal,
                      aiAudience,
                      aiTone,
                      aiToneCustom,
                      aiConversationStyle,
                      aiConversationStyleCustom,
                      aiStagesText,
                      aiObjectionsText,
                      aiDataFields,
                      aiQuoteFields,
                      aiGenerating,
                      aiSpec,
                      // MA-ARCH-142A: Loading state
                      aiMessageVariant,
                      aiGeneratingStartTime,
                    }}
                    actions={{
                      setAiStep,
                      setAiProvider,
                      setAiModel,
                      setAiApiKey,
                      setAiTitle,
                      setAiDescription,
                      setAiGoal,
                      setAiAudience,
                      setAiTone,
                      setAiToneCustom,
                      setAiConversationStyle,
                      setAiConversationStyleCustom,
                      setAiStagesText,
                      setAiObjectionsText,
                      setAiDataFields,
                      setAiQuoteFields,
                      onGenerateAiSpec,
                      onCreateFromAiSpec,
                      toast,
                    }}
                    presets={{ dataFieldLabelPresets, quoteFieldLabelPresets }}
                  />

                </div>
              </div>

              {scriptTypes.map((st) => (
                <TabsContent key={st.id} value={st.id} className="mt-5" data-testid={`playbook-scripts-tab-content-${st.id}`}>
                  {scripts.length === 0 ? (
                    <div className="muted" data-testid="playbook-scripts-empty-text">
                      No scripts yet.
                    </div>
                  ) : (
                    <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2" data-testid="playbook-scripts-grid">
                      {scripts.map((s) => (
                        <ScriptCard
                          key={s.id}
                          script={s}
                          canEdit={canEdit}
                          canUseSandbox={canUseSandbox}
                          onDelete={onDelete}
                          onDuplicate={onDuplicate}
                          userId={user?.id}
                        />
                      ))}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </>
      )}
    </div>
  );
}
